import type { Element, ElementContent, Root, Text } from "hast";
import type { Plugin } from "unified";
import { visit } from "unist-util-visit";

/**
 * Tag name of the wrapper element created around a run of consecutive citation
 * anchors. It is rendered by the `span` component override in
 * {@link reportCitationComponents} (see ReportSourceCitationGroup).
 */
export const GROUPED_CITATION_TAG = "span";

/**
 * Marker class placed on the grouping wrapper so the markdown `span` override
 * can tell a citation group apart from any other span in the content.
 */
export const CITATION_GROUP_CLASS = "report-citation-group";

/**
 * Anchor classes treated as inline citation badges that may be collapsed. These
 * match the page/source citations embedded in report section content (e.g.
 * `R-BE-000001-IME-Reports`). Section navigation links (`section-link`) are
 * intentionally excluded as they are standalone navigation, not page sources.
 */
export const GROUPABLE_CITATION_CLASSES = [
  "source-map-link",
  "bates-link",
  "page-reference",
];

/**
 * CSS selector matching a single groupable citation anchor. Shared with the
 * popover so a click on a badge can still be detected after collapsing.
 */
export const CITATION_ANCHOR_SELECTOR = GROUPABLE_CITATION_CLASSES.map(
  (className) => `a.${className}`
).join(", ");

/**
 * Minimum number of consecutive citation anchors required before they are
 * collapsed into a dropdown. A single, isolated badge is left inline.
 */
export const MIN_CITATIONS_TO_GROUP = 2;

function getClassNames(node: Element): string[] {
  const className = node.properties?.className;
  if (Array.isArray(className)) {
    return className.map(String);
  }
  if (typeof className === "string") {
    return className.split(/\s+/);
  }
  return [];
}

function isCitationAnchor(node: ElementContent): node is Element {
  if (node.type !== "element" || node.tagName !== "a") {
    return false;
  }
  const classes = getClassNames(node);
  return GROUPABLE_CITATION_CLASSES.some((className) =>
    classes.includes(className)
  );
}

function isWhitespace(node: ElementContent): node is Text {
  return node.type === "text" && node.value.trim() === "";
}

/**
 * Rehype plugin that collapses runs of consecutive inline citation anchors
 * (`source-map-link` / `bates-link`) — separated only by whitespace — into a
 * single wrapper element. The wrapper is rendered as a clickable dropdown so a
 * long row of badges no longer overloads the report text.
 *
 * Runs shorter than {@link MIN_CITATIONS_TO_GROUP} are left untouched.
 *
 * Must run AFTER `rehype-raw`, since the citation anchors arrive as raw HTML in
 * the section content and only become element nodes once raw HTML is parsed.
 */
export const rehypeGroupSourceCitations: Plugin<[], Root> = function () {
  return function (tree: Root) {
    visit(tree, "element", (node: Element) => {
      // Never re-process a wrapper we just created (its children are anchors).
      const classes = getClassNames(node);
      if (
        node.tagName === GROUPED_CITATION_TAG &&
        classes.includes(CITATION_GROUP_CLASS)
      ) {
        return;
      }

      const children = node.children;
      if (!children.some(isCitationAnchor)) {
        return;
      }

      const newChildren: ElementContent[] = [];
      let index = 0;

      while (index < children.length) {
        const child = children[index];

        if (!isCitationAnchor(child)) {
          newChildren.push(child);
          index += 1;
          continue;
        }

        // Collect a maximal run of citation anchors separated only by
        // whitespace text nodes (interleaving whitespace is dropped).
        const anchors: Element[] = [child];
        let lookahead = index + 1;
        let lastAnchorIndex = index;
        while (lookahead < children.length) {
          const next = children[lookahead];
          if (isCitationAnchor(next)) {
            anchors.push(next);
            lastAnchorIndex = lookahead;
            lookahead += 1;
          } else if (isWhitespace(next)) {
            lookahead += 1;
          } else {
            break;
          }
        }

        if (anchors.length >= MIN_CITATIONS_TO_GROUP) {
          newChildren.push({
            type: "element",
            tagName: GROUPED_CITATION_TAG,
            properties: { className: [CITATION_GROUP_CLASS] },
            children: anchors,
          });
          // Resume just after the last anchor so any whitespace that trailed
          // the run is preserved as a normal text node.
          index = lastAnchorIndex + 1;
        } else {
          newChildren.push(child);
          index += 1;
        }
      }

      node.children = newChildren;
    });
  };
};
